/**
 * MusicView (#448) — full-screen music player UI.
 *
 * Replaces the small `NowPlayingPill` (which was 32px tall, in the
 * bottom-right, easy to miss). Bas's testing-feedback (2026-05-07):
 * elderly users couldn't find it; the dismiss control didn't reliably
 * stop music; needed bigger play/pause + visible volume + animation
 * that signals "music is playing".
 *
 * Visual + behaviour decisions (auto-resolved when "do them all" was
 * authorised):
 *   Q1 animation = animated 5-bar EQ (universal music affordance,
 *      pure CSS keyframe, freezes on pause)
 *   Q2 layout    = stacked vertical (play centred, volume below)
 *   Q3 dismiss   = top-right X (consistent with VideoOverlay /
 *      PhotoCarousel close)
 *   Q4 phone     = full-screen on every viewport (pill removed)
 *   Q5 voice "stop" = NOT supported mid-music (mic is closed during
 *      music per #430; dismiss button is the path)
 *
 * Imperative coordination via `musicPlayerRef`: this component is a
 * thin presentation layer; real audio state lives in `MusicPlayer`
 * (the YouTube iframe). MusicView's onStop / onPause / onResume /
 * onVolumeChange callbacks invoke MusicPlayer's imperative methods.
 *
 * The dismiss-stops-music guarantee: pressing X (or the bigger Done
 * pill at the bottom) calls onStop which is responsible for both
 * clearing the nowPlaying React state AND calling
 * musicPlayerRef.current.stop() — wired in TabletApp.
 */
import React, { useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { useActivityBox } from './ActivityFrame';

const BRAND_ORANGE = '#E8845A';
const IVORY = '#F8F0DC';

// #441 Phase 2 — MusicView i18n bucket.
function _useStrings() {
  const ctx = useLang();
  const s = ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.musicview;
  return {
    nowPlaying: (s && s.nowPlaying) || 'Now playing',
    fallbackTitle: (s && s.fallbackTitle) || 'Music',
    play: (s && s.play) || 'Play',
    pause: (s && s.pause) || 'Pause',
    volumeDown: (s && s.volumeDown) || 'Volume down',
    volumeUp: (s && s.volumeUp) || 'Volume up',
    done: (s && s.done) || 'Done',
    closeAria: (s && s.closeAria) || 'Close music player',
  };
}


function EqBars({ playing }) {
  // 5 vertical bars with staggered keyframe delays. Frozen (no
  // animation) when paused. Pure CSS, no audio analysis.
  const bars = [0, 1, 2, 3, 4];
  return (
    <>
      <style>{`
        @keyframes mv-eq-bar {
          0%, 100% { transform: scaleY(0.35); }
          50%      { transform: scaleY(1); }
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 8,
          height: 80,
          marginBottom: 12,
        }}
      >
        {bars.map((i) => (
          <div
            key={i}
            style={{
              width: 12,
              height: 80,
              borderRadius: 6,
              background: BRAND_ORANGE,
              transformOrigin: 'bottom',
              animation: playing
                ? `mv-eq-bar ${0.7 + (i % 3) * 0.2}s infinite ease-in-out`
                : 'none',
              animationDelay: `${i * 0.12}s`,
              transform: playing ? undefined : 'scaleY(0.35)',
              opacity: playing ? 1 : 0.55,
              transition: 'opacity 0.25s ease',
            }}
          />
        ))}
      </div>
    </>
  );
}


export default function MusicView({
  // Track info (passed straight through from `nowPlaying` state).
  title = '',
  // Player imperative ref (parent's musicPlayerRef). MusicView calls
  // its methods directly — no extra plumbing needed.
  playerRef,
  // #1089 — close (X) is owned by the ActivityFrame now; the parent's
  // frame onClose bundles stop() + clearing nowPlaying (the dismiss-
  // stops-music guarantee lives there), so MusicView takes no onStop.
}) {
  // Read the measured ActivityFrame content slot. We branch into a
  // two-column landscape layout when the slot is wider than tall AND
  // tall enough only for a compact single column — this matches the
  // Samsung A9 in landscape (CSS box ~696×399 inside ActivityFrame),
  // where the original single-column ~500px content overflowed the
  // 399px height and the volume row was clipped off the bottom (the
  // older `margin: auto` "scroll-from-top" comment was wrong — flex
  // margin:auto centres AND clips on both sides when overflowing).
  const { width: boxW, height: boxH } = useActivityBox();
  const isTwoCol = boxW > 0 && boxH > 0 && boxW > boxH && boxW >= 600;
  const [paused, setPaused] = useState(false);
  // Volume mirrored locally for the slider; synced from the player on
  // mount + reset, then driven by the slider. YouTube range is 0..100.
  const [volume, setVolume] = useState(70);

  // Sync volume to the player on first paint + whenever the slider
  // changes locally.
  useEffect(() => {
    const p = playerRef && playerRef.current;
    if (p && typeof p.setVolume === 'function') {
      p.setVolume(volume);
    }
  }, [volume, playerRef]);

  // Pull the player's actual volume on mount so the slider starts in
  // sync with whatever default YouTube set.
  useEffect(() => {
    const p = playerRef && playerRef.current;
    if (!p || typeof p.getVolume !== 'function') return;
    try {
      const v = p.getVolume();
      if (typeof v === 'number') setVolume(v);
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tr = _useStrings();

  const handleTogglePlay = () => {
    const p = playerRef && playerRef.current;
    if (!p) return;
    if (paused) {
      p.resume();
      setPaused(false);
    } else {
      p.pause();
      setPaused(true);
    }
  };

  const adjustVolume = (delta) => {
    setVolume((v) => Math.max(0, Math.min(100, v + delta)));
  };

  const trackBlock = (
    <>
      <div style={styles.label}>{tr.nowPlaying}</div>
      <div style={styles.title}>{title || tr.fallbackTitle}</div>
      <EqBars playing={!paused} />
    </>
  );

  const controlsBlock = (
    <>
      {/* BIG play / pause button (Q2) */}
      <button
        onClick={handleTogglePlay}
        aria-label={paused ? tr.play : tr.pause}
        style={{
          ...styles.playBtn,
          background: paused ? BRAND_ORANGE : '#E5BB7A',
        }}
      >
        <span style={styles.playIcon}>{paused ? '▶' : '⏸'}</span>
      </button>
      <div style={styles.playLabel}>{paused ? tr.play : tr.pause}</div>

      {/* Volume row */}
      <div style={styles.volumeRow}>
        <button
          onClick={() => adjustVolume(-10)}
          aria-label={tr.volumeDown}
          style={styles.volBtn}
        >−</button>
        <div style={styles.volTrack}>
          <div style={{ ...styles.volFill, width: `${volume}%` }} />
        </div>
        <button
          onClick={() => adjustVolume(+10)}
          aria-label={tr.volumeUp}
          style={styles.volBtn}
        >+</button>
        <span style={styles.volValue}>{Math.round(volume)}</span>
      </div>
    </>
  );

  if (isTwoCol) {
    // Wide-and-short slot (A9 landscape, 696×399). Split into:
    //   left  → track info (label / title / EQ bars), centred.
    //   right → controls (play button + volume row), centred.
    // Both columns vertically centred in the available height; no
    // scrolling required because each column's content is well under
    // the typical ~399px landscape height.
    return (
      <div
        style={styles.backdropTwoCol}
        data-testid="music-view-two-col"
      >
        <div style={styles.col}>{trackBlock}</div>
        <div style={styles.col}>{controlsBlock}</div>
      </div>
    );
  }

  // Portrait / tall-enough slot — single centred column.
  return (
    <div style={styles.backdrop} data-testid="music-view-single-col">
      <div style={styles.centerCol}>
        {trackBlock}
        {controlsBlock}
      </div>
    </div>
  );
}


const _gradient = 'linear-gradient(180deg, #1a0f08 0%, #2a1a0e 100%)';

const styles = {
  backdrop: {
    // #1089 — fills the ActivityFrame content slot; the frame owns the
    // full-screen positioning + z-index + close. Single-column path
    // (portrait, or landscape with enough height).
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    background: _gradient,
    color: IVORY,
    display: 'flex',
    flexDirection: 'column',
    // `safe center` — centre vertically when content fits; on overflow,
    // anchor to the start so the top is reachable and the rest scrolls.
    // Replaces the older `margin: auto` on centerCol, which clipped on
    // both sides when content exceeded the slot height (the original
    // landscape-too-short bug — fixed for landscape by the two-column
    // path; safe center is a belt-and-braces for the portrait path).
    alignItems: 'safe center',
    justifyContent: 'safe center',
    overflowY: 'auto',
    padding: 'clamp(16px, 4vw, 48px)',
  },
  centerCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 520,
    gap: 16,
  },
  // Two-column landscape — wide-and-short slot (e.g. Samsung A9
  // landscape, ~696×399 inside ActivityFrame). Track info on the left,
  // controls on the right; both columns centred vertically.
  backdropTwoCol: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    background: _gradient,
    color: IVORY,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'safe center',
    justifyContent: 'safe center',
    gap: 'clamp(16px, 4vw, 56px)',
    padding: 'clamp(12px, 3vw, 32px)',
    overflow: 'hidden',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 440,
    gap: 12,
  },
  label: {
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    opacity: 0.65,
    color: IVORY,
  },
  title: {
    fontSize: 'clamp(22px, 3.5vw, 30px)',
    fontWeight: 600,
    textAlign: 'center',
    lineHeight: 1.3,
    marginBottom: 8,
    wordBreak: 'break-word',
  },
  playBtn: {
    width: 140,
    height: 140,
    borderRadius: '50%',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 24px rgba(232,132,90,0.45)',
    transition: 'background 0.2s ease, transform 0.1s ease',
    padding: 0,
  },
  playIcon: {
    fontSize: 64,
    lineHeight: 1,
    transform: 'translateY(-2px)',
  },
  playLabel: {
    fontSize: '0.95rem',
    fontWeight: 600,
    opacity: 0.85,
  },
  volumeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    marginTop: 24,
  },
  volBtn: {
    minWidth: 60,
    minHeight: 60,
    width: 60,
    height: 60,
    borderRadius: '50%',
    border: '1px solid rgba(248,240,220,0.28)',
    background: 'rgba(255,255,255,0.06)',
    color: IVORY,
    fontSize: 28,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  volTrack: {
    flex: 1,
    height: 12,
    borderRadius: 6,
    background: 'rgba(255,255,255,0.10)',
    overflow: 'hidden',
  },
  volFill: {
    height: '100%',
    background: BRAND_ORANGE,
    transition: 'width 0.15s ease',
  },
  volValue: {
    minWidth: 38,
    textAlign: 'right',
    fontSize: '0.95rem',
    fontWeight: 600,
    opacity: 0.85,
  },
  closeBtn: {
    position: 'absolute',
    top: 'clamp(16px, 3vw, 28px)',
    right: 'clamp(16px, 3vw, 28px)',
    minWidth: 60,
    minHeight: 60,
    borderRadius: '50%',
    border: '1px solid rgba(248,240,220,0.28)',
    background: 'rgba(255,255,255,0.06)',
    color: IVORY,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    fontSize: 28,
    lineHeight: 1,
  },
  doneBtn: {
    marginTop: 24,
    padding: '14px 36px',
    minHeight: 60,
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#1a0f08',
    background: IVORY,
    border: 'none',
    borderRadius: 32,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
  },
};
